import enhancedApiService from './enhancedApiService';

// Organization validation API functions
export const organizationValidationAPI = {
  // Check if organization name already exists
  checkOrganizationExists: async (organizationName) => {
    try {
      const response = await enhancedApiService.get('/api/organizations/check', {
        params: { name: organizationName.trim() }
      });
      
      return {
        exists: response.exists,
        suggestions: response.suggestions || [],
        conflictType: response.conflictType, // 'exact', 'similar', 'domain'
        message: response.message
      };
    } catch (error) {
      console.error('Organization check failed:', error);
      // Don't block signup if the check fails, but log the error
      return { exists: false, error: true };
    }
  },

  // Check if email domain belongs to existing organization
  checkEmailDomain: async (email) => {
    try {
      const domain = email.split('@')[1];
      if (!domain) return { hasConflict: false };

      const response = await enhancedApiService.get('/api/organizations/check-domain', {
        params: { domain }
      });
      
      return {
        hasConflict: response.hasConflict,
        organizationName: response.organizationName,
        adminContact: response.adminContact, // Obfuscated admin email
        message: response.message
      };
    } catch (error) {
      console.error('Domain check failed:', error);
      // Don't block signup if the check fails
      return { hasConflict: false, error: true };
    }
  },

  // Get organization by name (for admin reference)
  getOrganizationByName: async (organizationName) => {
    try {
      const response = await enhancedApiService.get('/api/organizations/by-name', {
        params: { name: organizationName.trim() }
      });
      
      return response.organization;
    } catch (error) {
      if (error.status === 404) {
        return null; // Organization not found (good for signup)
      }
      throw error;
    }
  }
};

// Utility functions for organization validation
export const organizationUtils = {
  // Normalize organization name for comparison
  normalizeOrganizationName: (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/\b(corp|corporation|inc|incorporated|ltd|limited|llc|company|co)\b/g, '') // Remove common suffixes
      .trim();
  },

  // Check if names are similar
  isSimilarName: (name1, name2, threshold = 0.8) => {
    const normalized1 = organizationUtils.normalizeOrganizationName(name1);
    const normalized2 = organizationUtils.normalizeOrganizationName(name2);
    
    // Simple similarity check (could be enhanced with Levenshtein distance)
    if (normalized1 === normalized2) return true;
    
    const similarity = organizationUtils.calculateSimilarity(normalized1, normalized2);
    return similarity >= threshold;
  },

  // Simple similarity calculation
  calculateSimilarity: (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = organizationUtils.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  },

  // Levenshtein distance calculation
  levenshteinDistance: (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  },

  // Extract domain from email
  getDomainFromEmail: (email) => {
    if (!email || !email.includes('@')) return null;
    return email.split('@')[1].toLowerCase();
  },

  // Check if domain is a common public domain
  isPublicEmailDomain: (domain) => {
    const publicDomains = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
      'icloud.com', 'aol.com', 'protonmail.com', 'mail.com'
    ];
    return publicDomains.includes(domain.toLowerCase());
  }
};